import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  AuthIgnoredPaths,
  ROUTES,
  Subscription,
  TOKEN_KEY,
  UserType,
} from "../Components/common";
import { useLocation, useNavigate } from "react-router-dom";
import { axiosInstant, endpoints } from "../api";
import { Spin } from "antd";
import AuthFormModal from "../Components/AuthPanel/AuthFormModal";
import moment from "moment";

interface ISubscription {
  name: Subscription;
  expiredAt: number;
  subscribedAt: number;
}
interface IUser {
  category?: UserType;
  email: string;
  isActive: string;
  name: string;
  token: string;
  _id: string;
  subscription?: null | ISubscription;
}

const initValues: IUser = {
  email: "",
  isActive: "false",
  name: "",
  token: "",
  _id: "",
};

interface IUserContext {
  user: IUser;
  setUser: (e?: IUser) => void;
  getMe: () => Promise<IUser | undefined>;
}

const AuthContext = createContext<IUserContext>({
  user: initValues,
  setUser: () => {},
  getMe: () => Promise.resolve(undefined),
});

export const useUser = () => useContext(AuthContext);

interface Props {
  children: ReactNode;
}
export const AuthProvider: FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(true);
  const [showLoginForm, updateLoginFormView] = useState(false);
  const [user, setUser] = useState<IUser>(initValues);

  const updateUserValue = (e?: IUser) => setUser(e || initValues);
  const handleLoginErr = (isManual?: boolean) => {
    if (isManual) updateLoginFormView(true);
    // Sign-in page rather than the marketing home page: whoever lands here needs
    // to sign in, and the home page has no obvious way to.
    else navigate(ROUTES.SIGN_IN);
  };

  const verifyUserSubscription = async (user: IUser) => {
    // Only block recruiters
    if (user.category === 'recruiter') {
      const { expiredAt = 0, name } = user.subscription || {};
      if (name === Subscription.SPECIAL) return; // developer/admin bypass
      if (expiredAt < moment().valueOf()) {
        navigate(ROUTES.SUBSCRIBE, { state: { paymentRequired: true } });
        return;
      }
    }
  };

  const getMe = async (isManual?: boolean) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      handleLoginErr(isManual);
      return;
    }

    try {
      const { data } = await axiosInstant.get(endpoints.getMe);
      if (!data?.token) handleLoginErr(isManual);
      else {
        if (data.category === undefined || data.category === null) {
          navigate(ROUTES.FINISH_SIGNUP);
          return;
        }
        console.log("User data:", data);
        setUser(data);
        localStorage.setItem(TOKEN_KEY, data.token);
        verifyUserSubscription(data);
      }
      setLoading(false);
      return data;
    } catch (err: any) {
      console.error('getMe error:', err);
      if (err?.response?.status === 401 || err?.response?.status === 404) {
        // Only clear on authentication errors
        setUser(initValues);
        localStorage.removeItem(TOKEN_KEY);
        if (!isIgnoredPath(pathname)) {
          navigate(ROUTES.SIGN_IN);
        }
      } else {
        // For other errors (network, etc.), keep the user logged in
        console.log('Network or server error, keeping user logged in');
      }
      setLoading(false);
      return;
    }
  };

  // Helper to check if path should be ignored for auth
  const isIgnoredPath = (path: string) => {
    return AuthIgnoredPaths.some(ignoredPath => 
      path === ignoredPath || path.startsWith(ignoredPath + '?') || path.startsWith('/jobs/')
    );
  };

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Load the session whenever a token exists and isn't loaded yet - on every
    // path, public ones included. This used to skip the load entirely on
    // "ignored" paths (the public job board and job detail pages, so a
    // signed-out visitor could browse without being bounced), which also meant
    // a signed-IN candidate opening a job page fresh - a new tab, a shared
    // link - never got their session populated: `user.token` stayed empty and
    // the Apply button told them they weren't logged in. isIgnoredPath still
    // does its other job below, in getMe's catch: a public page never force-
    // navigates a visitor away just because their token turned out stale.
    if (token && !user.token) {
      getMe();
    } else {
      setLoading(false);
    }
  }, [pathname]);

  if (loading) return <Spin spinning fullscreen percent="auto" />;
  return (
    <AuthContext.Provider value={{ user, setUser: updateUserValue, getMe }}>
      {children}
      {showLoginForm && (
        <AuthFormModal onCancel={() => updateLoginFormView(false)} />
      )}
    </AuthContext.Provider>
  );
};
